import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import VariableRail from './VariableRail'
import GroupsBuilder from './GroupsBuilder'
import CrossBuilder from './CrossBuilder'
import { expandCrossedNet } from './expandCrossedNet'
import {
  crossToLevels,
  definitionToBuilder,
  emptyBuilder,
  groupsToItems,
  suggestName,
  type BuilderMode,
  type BuilderState,
} from './builderModel'
import { useVariableCatalog } from './useBuilderData'
import useConstructs, { ConstructApiError } from '../../hooks/useConstructs'
import type { ConstructDefinition, ConstructSaveResult, JobConstruct } from '../../types/gotypes'

// ConstructEditorDialog is the construct builder: the create/edit surface
// for a construct (a variable defined by rules over other variables, rebuilt
// on every import). People build it one of two ways — Codes (one code per
// category, each a rule, as in Ruby's Construct Variables grid) or Cross two
// variables — with the job's variables on the left. It shows no respondent counts and never
// calls the construct preview: counting reads every case in the job (4-18
// seconds per call on Sky's Prius job), so counts are seen in a table once
// the construct is created. Saved definitions are unchanged: lines are code lists,
// a cross is a crossed net (design/2026-09-15-constructs-and-sav-import.md).
export interface ConstructEditorDialogProps {
  open: boolean
  onClose: () => void
  customer: string
  job: string
  construct?: JobConstruct // present when editing
  // The folder a NEW variable is filed under (My Constructs when omitted; the
  // Variables page always leaves it there). An edit keeps the construct's own.
  defaultFolder?: string
  // The saved construct plus, on update, each dependent's rebuild outcome.
  onSaved?: (row: ConstructSaveResult) => void
}

const constructCodes = {
  invalidRule: 'invalid_rule',
  cycle: 'cycle',
  crossedNetMismatch: 'crossed_net_mismatch',
  nameTaken: 'name_taken',
  alreadyExists: 'already_exists',
  jobBusy: 'job_busy',
} as const

const NAME_RULE = /^[a-z][a-z0-9_]{0,63}$/

// Each way of building is a tab over the right-hand pane, and its guidance
// sits at the top of the tab (the rail on the left is just search and tree).
const modes: { value: BuilderMode, label: string, guidance: string }[] = [
  {
    value: 'groups',
    label: 'Codes',
    guidance: 'Each code becomes one value of the new variable. Tick questions or answers in the tree, then press a green +. Edit a code\'s rule directly if you prefer typing.',
  },
  {
    value: 'cross',
    label: 'Cross two variables',
    guidance: 'Every combination of two questions\' answers becomes a code, for example “Male x London”. Tick a question (all its answers) or some of its answers in the tree, then press a green + to use it as the first or second question; tick two questions to fill both. Build a table with it after you save to see how many respondents are in each.',
  },
]

const tabId = (mode: BuilderMode) => `construct-mode-tab-${mode}`
const panelId = (mode: BuilderMode) => `construct-mode-panel-${mode}`

export const ConstructEditorDialog: React.FC<ConstructEditorDialogProps> = ({
  open, onClose, customer, job, construct, defaultFolder, onSaved,
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isEdit = Boolean(construct)
  const { create, update, isSaving } = useConstructs({ customer, job })
  const catalog = useVariableCatalog(customer, job)

  const [state, setState] = useState<BuilderState>(() => (construct ? definitionToBuilder(construct.definition) : emptyBuilder()))
  const [name, setName] = useState(construct?.name || '')
  const [nameTouched, setNameTouched] = useState(isEdit)
  const [nameError, setNameError] = useState<string | null>(null)
  const [banner, setBannerInBuilder] = useState<string | null>(null)
  // A new construct is named on Create, in a small dialog over the builder.
  // Its server error (anything but a name clash) shows there, not in banner.
  const [naming, setNaming] = useState(false)
  const [namingError, setNamingError] = useState<string | null>(null)
  const folder = construct?.folder || defaultFolder || 'My Constructs'

  const variableLabel = (n: string) => catalog.byName.get(n)?.label || n

  // Reopening a saved variable: once the job's variables have loaded, fetch
  // the categories of the ones it uses so its chips read "Male", not "1".
  const usedVariables = useMemo(() => {
    if (!construct) return []
    if (construct.sources?.length) return construct.sources
    const def = construct.definition
    return [...new Set([
      ...(def.levels || []).map(l => l.variable),
      ...def.items.flatMap(i => i.filter.match(/[a-z][a-z0-9_]*(?=\()/gi) || []),
    ])]
  }, [construct])
  useEffect(() => {
    if (catalog.variables.length === 0) return
    usedVariables.forEach(v => { catalog.ensureVariable(v) })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger: the variable list arriving
  }, [catalog.variables.length])

  // What would be saved (empty groups aren't).
  const built = useMemo(() => {
    let definition: ConstructDefinition | null = null
    let labels: string[] = []
    if (state.mode === 'groups') {
      const items = groupsToItems(state.groups)
      labels = items.map(i => i.label)
      definition = { name, description: construct?.description || '', kind: 'codeList', items }
    } else if (state.mode === 'cross' && state.cross?.rows && state.cross.cols) {
      const cats = catalog.categoriesOf(state.cross.rows) || []
      const levels = crossToLevels(
        { variable: state.cross.rows, codes: state.cross.rowCodes.map(code => ({ code, label: cats.find(c => c.code === code)?.label || state.cross!.rowLabels[code] || code })) },
        { variable: state.cross.cols, groups: state.cross.colGroups },
      )
      const items = expandCrossedNet(levels)
      labels = [variableLabel(state.cross.rows), 'by', variableLabel(state.cross.cols)]
      definition = { name, description: construct?.description || '', kind: 'crossedNet', items, levels }
    }
    return { definition, labels }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, name, catalog.categoriesOf, catalog.byName, construct?.description])

  const itemCount = built.definition?.items.length || 0

  const suggested = suggestName(built.labels.length > 0 && state.mode !== 'groups' ? built.labels : built.labels.slice(0, 2))
  const effectiveName = nameTouched ? name : suggested

  // The tree's ticks are one selection shared by both tabs, so they are
  // kept when switching tab: each tab shows where they can go.
  const setMode = (mode: BuilderMode) => setState(s => ({ ...s, mode }))

  const applyApiError = (err: ConstructApiError) => {
    // While naming, every error shows in the name dialog, which stays open.
    const setBanner = naming ? setNamingError : setBannerInBuilder
    switch (err.code) {
      case constructCodes.nameTaken:
        setNameError('That name is already used in this job.')
        break
      case constructCodes.alreadyExists:
        setNameError('A variable with this name already exists.')
        break
      case constructCodes.invalidRule:
        setBanner(err.errors?.join('; ') || 'One or more codes have a rule Carbon cannot read.')
        break
      case constructCodes.cycle:
        setBanner(`This variable would depend on itself: ${(err.path || []).join(' → ')}`)
        break
      case constructCodes.crossedNetMismatch:
        setBanner("The crossed codes don't match the variables they came from. Pick the variables again.")
        break
      case constructCodes.jobBusy:
        setBanner('This job is busy with another change. Try again in a moment.')
        break
      default:
        setBanner(err.message || 'The variable could not be saved.')
    }
  }

  const nameProblem = effectiveName && !NAME_RULE.test(effectiveName)
    ? 'Start with a letter; use lower-case letters, digits and underscores.'
    : null
  // Create construct / Save changes: there is something valid to save. A new
  // construct's name is asked for after this, in the name dialog.
  const canSave = Boolean(built.definition && itemCount > 0 && !isSaving)
  const canCreate = Boolean(canSave && effectiveName && !nameProblem)

  const handleSave = async () => {
    if (!built.definition) return
    setNameError(null)
    setBannerInBuilder(null)
    setNamingError(null)
    const definition = { ...built.definition, name: effectiveName }
    try {
      const row = isEdit && construct
        ? await update(construct.name, definition, folder)
        : await create(definition, folder)
      onSaved?.(row)
      setNaming(false)
      onClose()
    } catch (err) {
      applyApiError(err as ConstructApiError)
    }
  }

  // A saved construct cannot be renamed, so Save changes just saves. A new one
  // opens the name dialog, starting from the suggested name.
  const handlePrimary = () => {
    if (isEdit) {
      handleSave()
      return
    }
    setNameError(null)
    setNamingError(null)
    setNaming(true)
  }
  const submitName = () => {
    if (canCreate) handleSave()
  }
  // Esc or Cancel goes back to the builder with nothing lost, the typed name
  // included.
  const cancelNaming = () => {
    if (!isSaving) setNaming(false)
  }

  const mode = modes.find(m => m.value === state.mode)!

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      aria-labelledby="variable-builder-title"
      PaperProps={{ sx: { maxWidth: fullScreen ? undefined : 1320, height: fullScreen ? '100%' : 'min(820px, 92vh)', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto', borderRadius: fullScreen ? 0 : 3 } }}
    >
      <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography id="variable-builder-title" component="h2" sx={{ flex: '1 1 0', minWidth: 0, fontSize: 18, fontWeight: 650, letterSpacing: '-0.01em' }}>
          {isEdit ? `Edit ${construct?.name}` : 'New construct'}
        </Typography>
        <IconButton onClick={onClose} aria-label="close" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'clamp(340px, 38%, 440px) minmax(0, 1fr)' }, gridTemplateRows: { xs: 'auto 1fr', md: '1fr' }, minHeight: 0, overflow: { xs: 'auto', md: 'hidden' } }}>
        <VariableRail
          nodes={catalog.nodes}
          onOpenVariable={name => { catalog.ensureVariable(name) }}
          checkboxes={{ getCheckboxState: catalog.getCheckboxState, onNodeCheckedToggle: catalog.onNodeCheckedToggle }}
          isLoading={catalog.isLoading}
        />
        <Box component="main" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Tabs
            value={state.mode}
            onChange={(_e, v: BuilderMode) => setMode(v)}
            aria-label="How to build the variable"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 3, borderBottom: 1, borderColor: 'divider', minHeight: 40, flexShrink: 0 }}
          >
            {modes.map(m => (
              <Tab key={m.value} value={m.value} label={m.label} id={tabId(m.value)} aria-controls={panelId(m.value)} sx={{ minHeight: 40, textTransform: 'none' }} />
            ))}
          </Tabs>
          <Box
            role="tabpanel"
            id={panelId(state.mode)}
            aria-labelledby={tabId(state.mode)}
            sx={{ flex: 1, minHeight: 0, overflowY: { md: 'auto' }, px: 3, py: 2.5, display: 'grid', alignContent: 'start', gap: 2 }}
          >
            {banner && <Alert severity="error" onClose={() => setBannerInBuilder(null)}>{banner}</Alert>}

            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '62ch' }}>
              {mode.guidance}
            </Typography>

            {state.mode === 'groups' && (
              <GroupsBuilder
                groups={state.groups}
                onChange={groups => setState(s => ({ ...s, groups }))}
                categoriesOf={catalog.categoriesOf}
                labelOf={catalog.labelOf}
                variableLabel={variableLabel}
                onOpenVariable={name => { catalog.ensureVariable(name) }}
                selection={catalog.selection}
                onSelectionUsed={catalog.clearSelection}
              />
            )}
            {state.mode === 'cross' && (
              <CrossBuilder
                cross={state.cross}
                onChange={cross => setState(s => ({ ...s, cross }))}
                categoriesOf={catalog.categoriesOf}
                variableLabel={variableLabel}
                onOpenVariable={catalog.ensureVariable}
                selection={catalog.selection}
                onSelectionUsed={catalog.clearSelection}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box component="footer" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
          <Typography variant="body2" color="text.secondary"><Box component="b" sx={{ color: 'text.primary' }}>{itemCount}</Box> {itemCount === 1 ? 'code' : 'codes'}</Typography>
          {state.mode === 'groups' && itemCount > 1 && <Typography variant="body2" color="text.secondary">A respondent can be in more than one code</Typography>}
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handlePrimary} disabled={!canSave}>
            {isSaving && isEdit ? 'Saving…' : isEdit ? 'Save changes' : 'Create construct'}
          </Button>
        </Box>
      </Box>

      {!isEdit && (
        <Dialog
          open={naming}
          onClose={cancelNaming}
          fullWidth
          maxWidth="xs"
          aria-labelledby="construct-name-title"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle id="construct-name-title" sx={{ fontSize: 18, fontWeight: 650 }}>Name the new variable</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2 }}>
            {namingError && <Alert severity="error" onClose={() => setNamingError(null)}>{namingError}</Alert>}
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Variable name"
              value={effectiveName}
              onChange={e => { setName(e.target.value); setNameTouched(true); setNameError(null) }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitName()
                }
              }}
              error={Boolean(nameError || nameProblem)}
              helperText={nameError || nameProblem || `Saved in ${folder}`}
              inputProps={{ 'aria-label': 'construct name' }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cancelNaming} disabled={isSaving}>Cancel</Button>
            <Button variant="contained" color="info" onClick={submitName} disabled={!canCreate}>
              {isSaving ? 'Saving…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  )
}

export default ConstructEditorDialog
